import chatLastMessageOf from '@/function/conversation/chat/chat.last.message';
import { chatApi } from '@/store/api/conversation/chat/chat.api';
import type { TypeAppDispatch, TypeRootState } from '@/store/store';
import type { TypeAttachment } from '@/types/attachment/types.attachment';
import type { TypeChatPresenceUpdate } from '@/types/conversation/types.conversation.chat.channel';
import type { TypeChatMessage } from '@/types/conversation/types.conversation.chat.message';

const newestFirst = (a: TypeChatMessage, b: TypeChatMessage): number =>
    new Date(b.at).getTime() - new Date(a.at).getTime() || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);

const meOf = (getState: () => TypeRootState): string | null => getState().auth.user?.authUserId ?? null;

const merge = (current: TypeChatMessage, incoming: TypeChatMessage): TypeChatMessage => {
    if (incoming.poll && incoming.poll.mine === null && current.poll?.mine) return { ...incoming, poll: { ...incoming.poll, mine: current.poll.mine } };
    return incoming;
};

export const writeChatMessage = (dispatch: TypeAppDispatch, message: TypeChatMessage) => {
    dispatch((inner: TypeAppDispatch, getState: () => TypeRootState) => {
        const me = meOf(getState);
        let isNew = false;
        const cached = chatApi.endpoints.listChatMessages.select(message.channelId)(getState());
        if (!cached.data?.pages.length) isNew = true;
        inner(chatApi.util.updateQueryData('listChatMessages', message.channelId, (draft) => {
            if (!draft.pages.length) return;
            for (const page of draft.pages) {
                const index = page.items.findIndex((item) => item.id === message.id || (message.clientRef && item.clientRef === message.clientRef && item.pending));
                if (index >= 0) {
                    page.items[index] = merge(page.items[index], message);
                    return;
                }
            }
            isNew = true;
            draft.pages[0].items.push(message);
            draft.pages[0].items.sort(newestFirst);
        }));
        inner(chatApi.util.updateQueryData('listChatChannels', undefined, (draft) => {
            const channel = draft.channels.find((entry) => entry.id === message.channelId);
            if (!channel) return;
            if (!channel.lastMessageAt || channel.lastMessageAt < message.at) channel.lastMessageAt = message.at;
            if (!channel.lastMessage || channel.lastMessage.at <= message.at || channel.lastMessage.id === message.id) channel.lastMessage = chatLastMessageOf(message);
            if (message.authorId === me) {
                if (!channel.lastReadAt || channel.lastReadAt < message.at) channel.lastReadAt = message.at;
                return;
            }
            const fresh = !channel.lastReadAt || channel.lastReadAt < message.at;
            if (!fresh || !isNew || message.kind === 'event') return;
            channel.unread.count += 1;
            if (!channel.unread.firstUnreadId) channel.unread.firstUnreadId = message.id;
            if (me && message.mentions.includes(me) && !channel.unread.mentionIds.includes(message.id)) channel.unread.mentionIds.push(message.id);
        }));
    });
};

export const dropChatMessage = (dispatch: TypeAppDispatch, ref: { id: string; channelId: string; reason?: 'deleted' | 'expired' }) => {
    let previous: TypeChatMessage | null = null;
    dispatch(chatApi.util.updateQueryData('listChatMessages', ref.channelId, (draft) => {
        for (const page of draft.pages) {
            page.items = page.items.filter((item) => item.id !== ref.id);
            for (const item of page.items) {
                if (item.replyTo?.id === ref.id) item.replyTo = { ...item.replyTo, text: '', files: 0, deleted: true };
                if (!previous || item.at > previous.at) previous = item;
            }
        }
    }));
    dispatch(chatApi.util.updateQueryData('listChatChannels', undefined, (draft) => {
        const channel = draft.channels.find((entry) => entry.id === ref.channelId);
        if (!channel) return;
        channel.unread.mentionIds = channel.unread.mentionIds.filter((id) => id !== ref.id);
        if (channel.unread.firstUnreadId === ref.id) channel.unread.firstUnreadId = null;
        if (channel.lastMessage?.id === ref.id) channel.lastMessage = previous ? chatLastMessageOf(previous) : null;
    }));
};

export const writeChatPresence = (dispatch: TypeAppDispatch, update: TypeChatPresenceUpdate) => {
    dispatch(chatApi.util.updateQueryData('listChatChannels', undefined, (draft) => {
        const member = draft.members.find((entry) => entry.id === update.userId);
        if (!member) return;
        member.presence = update.presence;
        if (update.lastSeenAt && (!member.lastSeenAt || member.lastSeenAt < update.lastSeenAt)) member.lastSeenAt = update.lastSeenAt;
    }));
};

export const patchChatMessageAttachments = (dispatch: TypeAppDispatch, holder: { id: string; channelId: string; attachments: TypeAttachment[] }) => {
    dispatch(chatApi.util.updateQueryData('listChatMessages', holder.channelId, (draft) => {
        for (const page of draft.pages) for (const item of page.items) if (item.id === holder.id) item.attachments = holder.attachments;
    }));
};

export const writeChatArchive = (dispatch: TypeAppDispatch, state: { channelId: string; archivedAt: string | null }) => {
    dispatch(chatApi.util.updateQueryData('listChatChannels', undefined, (draft) => {
        const channel = draft.channels.find((entry) => entry.id === state.channelId);
        if (channel) channel.archivedAt = state.archivedAt;
    }));
};

export const writeChatMute = (dispatch: TypeAppDispatch, state: { channelId: string; mutedUntil: string | null }) => {
    dispatch(chatApi.util.updateQueryData('listChatChannels', undefined, (draft) => {
        const channel = draft.channels.find((entry) => entry.id === state.channelId);
        if (channel) channel.mutedUntil = state.mutedUntil;
    }));
};

export const applyChatRead = (dispatch: TypeAppDispatch, read: { channelId: string; userId: string; lastReadAt: string }) => {
    dispatch((inner: TypeAppDispatch, getState: () => TypeRootState) => {
        if (read.userId !== meOf(getState)) {
            inner(chatApi.util.invalidateTags([{ type: 'ChatReceipts', id: read.channelId }]));
            inner(chatApi.util.updateQueryData('listChatReads', read.channelId, (draft) => {
                const entry = draft.find((item) => item.userId === read.userId);
                if (!entry) draft.push({ userId: read.userId, lastReadAt: read.lastReadAt });
                else if (entry.lastReadAt < read.lastReadAt) entry.lastReadAt = read.lastReadAt;
            }));
            return;
        }
        inner(chatApi.util.updateQueryData('listChatChannels', undefined, (draft) => {
            const channel = draft.channels.find((entry) => entry.id === read.channelId);
            if (!channel || (channel.lastReadAt && channel.lastReadAt >= read.lastReadAt)) return;
            channel.lastReadAt = read.lastReadAt;
            const latest = channel.lastMessageAt;
            if (!latest || latest <= read.lastReadAt) channel.unread = { count: 0, firstUnreadId: null, mentionIds: [] };
        }));
    });
};
